import { ENTITY_TYPES, ISearchResponse } from '@sales-center/common'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { ServiceConfig } from '../services/config'
import type { StoreData } from '../reducers/typings'
import { get, post, type APIReject, type APIError } from '../services/crud'

import type {
  ICancelReason,
  BestFitRequest,
  BestFitResponse,
  IEntityAppointment,
  GetAppointmentsInput,
  SchedulingCommitInput,
  SchedulingCommitResponse,
  AppointmentCompleteInput,
  AppointmentCompleteResponse,
  SchedulingEligibilityResponse,
  IEntityAppointmentSearchInput,
  IEntityAppointmentSearchResponse,
  GetAppointmentsResponse,
} from './typings'
import { formatTimeForSearch } from './utils'
import { updateEntityClosingRequirements } from '../closingRequirements'

const baseUrl = `${ServiceConfig.baseAPIUrl}${ServiceConfig.scheduling.scheduling}`

export const getSchedulingEligibility =
  createAsyncThunk<SchedulingEligibilityResponse>(
    'scheduling/eligibility',
    async (_, thunkApi) => {
      const response = await get(
        `${baseUrl}${ServiceConfig.scheduling.eligibility}`,
      )
      if (response.ok) {
        return await response.json()
      }
      return thunkApi.rejectWithValue(await response.json())
    },
  )

export const getAllEntityAppointments = createAsyncThunk<
  IEntityAppointment[],
  GetAppointmentsInput,
  { rejectValue: APIReject }
>('entityAppointments/getByEntityId', async (input, thunkApi) => {
  const { entityId, entityType } = input

  const response = await post(
    `${baseUrl}${ServiceConfig.scheduling.getEntityAppointments}`,
    input,
  )

  const data = await response.json()

  if (response.ok) {
    const appts: GetAppointmentsResponse[] = data

    return appts.map<IEntityAppointment>((a) => ({
      ...a,
      entityId,
      entityType,
      workTime: typeof a.workTime === 'string' ? new Date(a.workTime) : null,
    }))
  }

  return thunkApi.rejectWithValue({ status: response.status, ...data })
})

export const searchExistingAppointments = createAsyncThunk<
  IEntityAppointmentSearchResponse[],
  IEntityAppointmentSearchInput,
  { rejectValue: APIReject }
>('entityAppointments/search', async (input, thunkApi) => {
  const response = await post(
    `${baseUrl}${ServiceConfig.scheduling.searchExistingAppointments}`,
    input,
  )
  if (response.ok) {
    const items: IEntityAppointmentSearchResponse[] = (await response.json())
      .items

    // correct typings
    return items.map((i) => ({
      ...i,
      entityId: Number(i.entityId),
      entityType:
        i.entityType.toString() === 'Lead'
          ? ENTITY_TYPES.Lead
          : ENTITY_TYPES.Opportunity,
    }))
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const getAllCancelReasons = createAsyncThunk<
  ICancelReason[],
  void,
  { rejectValue: string }
>('cancelReasons/all', async (_, thunkApi) => {
  const response = await get(
    `${baseUrl}${ServiceConfig.scheduling.cancelReasons}`,
  )

  if (response.ok) {
    const payload: ISearchResponse<ICancelReason> = await response.json()
    return payload.items
  } else {
    const json = await response.json()
    return thunkApi.rejectWithValue(json)
  }
})

export const completeAppointment = createAsyncThunk<
  AppointmentCompleteResponse,
  AppointmentCompleteInput,
  { rejectValue: APIReject }
>('entityAppointments/complete', async (input, thunkApi) => {
  const { entityId, entityType, appointmentId, requestInput } = input

  const response = await post(
    `${baseUrl}${ServiceConfig.scheduling.appointment}/${appointmentId}/complete`,
    requestInput,
  )

  if (response.ok) {
    thunkApi.dispatch(
      updateEntityClosingRequirements({
        entityId,
        entityType,
      }),
    )

    return await response.json()
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const searchAppointmentsByLocation = createAsyncThunk<
  ISearchResponse<BestFitResponse>,
  BestFitRequest,
  { rejectValue: APIReject }
>('scheduling/search', async (params, thunkApi) => {
  const { externalSvcLocId, bestFitInput } = params

  const qstring = `?skip=0&take=10`

  const url = `${baseUrl}${ServiceConfig.scheduling.search(externalSvcLocId)}`

  const startTime = formatTimeForSearch(bestFitInput.startTime)
  const endTime = formatTimeForSearch(bestFitInput.endTime)

  const response = await post(`${url}${qstring}`, {
    ...bestFitInput,
    startTime,
    endTime,
  })

  if (response.ok) {
    const result: ISearchResponse<BestFitResponse> = await response.json()

    const items = (result.items ?? []).map((i) => ({
      ...i,
      workTime: new Date(i.workTime),
      alternateTimes: i.alternateTimes.map((a) => ({
        ...a,
        workTime: new Date(a.workTime),
      })),
    }))

    return {
      ...result,
      items,
    }
  }
  return thunkApi.rejectWithValue(await response.json())
})

export const commitAppointment = createAsyncThunk<
  SchedulingCommitResponse,
  { resultId: string; input: SchedulingCommitInput },
  { rejectValue: APIError }
>('scheduling/commit', async ({ resultId, input }, thunkApi) => {
  const { entityId, entityType } = input

  const response = await post(
    `${baseUrl}${ServiceConfig.scheduling.commit}/${resultId}`,
    input,
  )

  const data = await response.json()

  if (response.ok) {
    const { leadsState: leads, opportunitiesState: opps } =
      thunkApi.getState() as StoreData

    const isLead = entityType === ENTITY_TYPES.Lead

    const entityIds = (isLead ? leads.ids : opps.ids).map(Number)

    if (!entityIds.includes(entityId)) entityIds.push(entityId)

    // refetch existing appts
    if (entityIds.length > 0) {
      thunkApi.dispatch(
        searchExistingAppointments({
          entityIds,
          entityType: isLead ? 1 : 0,
        }),
      )
    }

    // update closing reqs
    thunkApi.dispatch(
      updateEntityClosingRequirements({
        entityId,
        entityType,
      }),
    )
    return await data
  }

  return thunkApi.rejectWithValue({ status: response.status, ...data })
})

export const getAppointment = createAsyncThunk<
  IEntityAppointment,
  { appointmentId: string; entityId: number; entityType: ENTITY_TYPES },
  { rejectValue: APIReject }
>('entityAppointments/get', async (input, thunkApi) => {
  const { appointmentId, entityId, entityType } = input

  const response = await get(
    `${baseUrl}${ServiceConfig.scheduling.appointment}/${appointmentId}`,
  )

  if (response.ok) {
    const appt: GetAppointmentsResponse = await response.json()

    return {
      ...appt,
      entityId,
      entityType,
      workTime: new Date(appt.workTime),
    }
  }

  return thunkApi.rejectWithValue(await response.json())
})
